import type { ExcelImportCategory, ExcelImportColumn, ExcelImportIssue, ExcelImportRow } from '../services/creditImportService';

/**
 * Client-side mirror of the server's spreadsheet import rules, re-run after every edit so the
 * review stays strict ("what you see is what lands"). The server re-validates on commit regardless.
 * Design: docs/plans/excel-credit-import.md, "Review step redesign brief".
 */

export const TITLE_MAX = 500;
export const PROVIDER_MAX = 255;
export const SPONSOR_MAX = 50;
export const NOTES_MAX = 2000;
export const MIN_YEAR = 1990;
export const MAX_HOURS = 999.9;

/** Unrecognized header → category id, or 'ignore' (explicit). Absent = unresolved, which blocks the import. */
export type ColumnMapping = Record<string, number | 'ignore'>;

/** A row as the review holds it: the server row plus the user's include / remove choices. */
export interface ImportRowDraft extends ExcelImportRow {
  /** False = the user left it skipped (duplicates start skipped). */
  include: boolean;
  /** Soft-removed: excluded from everything, restorable until commit. */
  removed: boolean;
  /** Issues only the server can judge (email → user), kept until re-parse. */
  serverIssues: ExcelImportIssue[];
}

export interface ValidationContext {
  mode: 'self' | 'tenant';
  deliveryMethods: string[];
  categoryIds: Set<number>;
  columnMapping: ColumnMapping;
  /** yyyy-MM-dd */
  today: string;
}

export function toDraft(row: ExcelImportRow): ImportRowDraft {
  return {
    ...row,
    include: !row.isDuplicate,
    removed: false,
    serverIssues: row.issues.filter((i) => i.field === 'email'),
  };
}

export function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

/** True when n is a non-negative multiple of 0.1 (compared at one decimal to dodge float noise). */
export function isTenths(n: number): boolean {
  if (!Number.isFinite(n) || n < 0) return false;
  return Math.abs(Math.round(n * 10) / 10 - n) < 0.0001;
}

export function roundTenths(n: number): number {
  return Math.round(n * 10) / 10;
}

function isValidIsoDate(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

/** Hours that will actually land for a row: recognized categories plus unrecognized columns mapped to one. */
export function effectiveHours(row: ImportRowDraft, columnMapping: ColumnMapping): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [categoryId, h] of Object.entries(row.hours)) {
    if (h > 0) out[categoryId] = roundTenths((out[categoryId] ?? 0) + h);
  }
  for (const [header, h] of Object.entries(row.unmappedHours)) {
    const mapped = columnMapping[header];
    if (typeof mapped === 'number' && h > 0) out[String(mapped)] = roundTenths((out[String(mapped)] ?? 0) + h);
  }
  return out;
}

export function totalHours(row: ImportRowDraft, columnMapping: ColumnMapping): number {
  return roundTenths(Object.values(effectiveHours(row, columnMapping)).reduce((s, h) => s + h, 0));
}

/** Hours sitting under columns the user has not decided on yet (neither mapped nor ignored). */
export function unresolvedHours(row: ImportRowDraft, columnMapping: ColumnMapping): number {
  let sum = 0;
  for (const [header, h] of Object.entries(row.unmappedHours)) {
    if (columnMapping[header] === undefined && h > 0) sum += h;
  }
  return roundTenths(sum);
}

export function validateRow(row: ImportRowDraft, ctx: ValidationContext): ExcelImportIssue[] {
  const issues: ExcelImportIssue[] = [...row.serverIssues];
  const add = (field: string, message: string) => issues.push({ field, message });

  if (ctx.mode === 'tenant' && !row.userId && !issues.some((i) => i.field === 'email')) {
    if (!row.email?.trim()) add('email', 'Email is required.');
    else add('email', 'No one in the firm has signed in with this email.');
  }

  const title = row.activityTitle?.trim() ?? '';
  if (!title) add('activityTitle', 'Activity title is required.');
  else if (title.length > TITLE_MAX) add('activityTitle', `Title is longer than ${TITLE_MAX} characters.`);

  const date = row.dateEarned ?? '';
  if (!date || !isValidIsoDate(date)) add('dateEarned', 'Enter a date (2025-03-15 or 3/15/2025).');
  else if (date > ctx.today) add('dateEarned', 'Date is in the future.');
  else if (Number(date.slice(0, 4)) < MIN_YEAR) add('dateEarned', `Date is before ${MIN_YEAR}.`);

  if ((row.providerName ?? '').length > PROVIDER_MAX) add('providerName', `Provider is longer than ${PROVIDER_MAX} characters.`);
  if ((row.nasbaSponsorId ?? '').length > SPONSOR_MAX) add('nasbaSponsorId', `NASBA sponsor ID is longer than ${SPONSOR_MAX} characters.`);
  if ((row.notes ?? '').length > NOTES_MAX) add('notes', `Notes are longer than ${NOTES_MAX} characters.`);

  if (row.deliveryMethod && !ctx.deliveryMethods.includes(row.deliveryMethod)) {
    add('deliveryMethod', 'Pick one of the listed delivery methods, or leave it blank.');
  }

  for (const [categoryId, h] of Object.entries(row.hours)) {
    if (h === null || h === undefined) continue;
    if (!ctx.categoryIds.has(Number(categoryId))) add(`hours:${categoryId}`, 'Unknown field of study.');
    else if (!isTenths(h)) add(`hours:${categoryId}`, h < 0 ? 'Hours cannot be negative.' : 'Hours go in tenths (1.5, not 1.25).');
    else if (h > MAX_HOURS) add(`hours:${categoryId}`, `Hours cannot exceed ${MAX_HOURS}.`);
  }
  for (const [header, h] of Object.entries(row.unmappedHours)) {
    if (ctx.columnMapping[header] === 'ignore') continue;
    if (!isTenths(h)) add(`unmapped:${header}`, h < 0 ? 'Hours cannot be negative.' : 'Hours go in tenths (1.5, not 1.25).');
    else if (h > MAX_HOURS) add(`unmapped:${header}`, `Hours cannot exceed ${MAX_HOURS}.`);
  }

  // "No hours" only when no hours cell is itself flagged, and only once unresolved columns are decided:
  // while a column is undecided the gate is blocked at the column, not row by row.
  const hasHoursCellIssue = issues.some((i) => i.field.startsWith('hours:') || i.field.startsWith('unmapped:'));
  const landing = Object.keys(effectiveHours(row, ctx.columnMapping)).length > 0;
  const pending = unresolvedHours(row, ctx.columnMapping) > 0;
  if (!hasHoursCellIssue && !landing && !pending) {
    add('hours', 'At least one field of study needs hours.');
  }

  return issues;
}

export type RowStatus = 'ready' | 'skipped' | 'error' | 'removed';

/** Removed wins, then the user's skip choice, then issues. Unticking a bad row is a valid way out. */
export function rowStatus(issues: ExcelImportIssue[], include: boolean, removed: boolean): RowStatus {
  if (removed) return 'removed';
  if (!include) return 'skipped';
  if (issues.length > 0) return 'error';
  return 'ready';
}

export interface ReviewCounts {
  ready: number;
  readyHours: number;
  skipped: number;
  /** Skipped rows that were flagged as duplicates (subset of skipped). */
  skippedDuplicates: number;
  errors: number;
  removed: number;
}

export function countRows(rows: ImportRowDraft[], issuesByRow: Record<number, ExcelImportIssue[]>, columnMapping: ColumnMapping): ReviewCounts {
  const counts: ReviewCounts = { ready: 0, readyHours: 0, skipped: 0, skippedDuplicates: 0, errors: 0, removed: 0 };
  for (const row of rows) {
    const status = rowStatus(issuesByRow[row.rowNumber] ?? [], row.include, row.removed);
    if (status === 'ready') {
      counts.ready++;
      counts.readyHours = roundTenths(counts.readyHours + totalHours(row, columnMapping));
    } else if (status === 'skipped') {
      counts.skipped++;
      if (row.isDuplicate) counts.skippedDuplicates++;
    } else if (status === 'error') {
      counts.errors++;
    } else {
      counts.removed++;
    }
  }
  return counts;
}

/** Hours that will land, by category, across rows that are ready. */
export function hoursByCategory(
  rows: ImportRowDraft[],
  issuesByRow: Record<number, ExcelImportIssue[]>,
  columnMapping: ColumnMapping,
  categories: ExcelImportCategory[],
): { category: ExcelImportCategory; hours: number }[] {
  const totals: Record<string, number> = {};
  for (const row of rows) {
    if (rowStatus(issuesByRow[row.rowNumber] ?? [], row.include, row.removed) !== 'ready') continue;
    for (const [categoryId, h] of Object.entries(effectiveHours(row, columnMapping))) {
      totals[categoryId] = roundTenths((totals[categoryId] ?? 0) + h);
    }
  }
  return categories
    .filter((c) => (totals[String(c.id)] ?? 0) > 0)
    .map((c) => ({ category: c, hours: totals[String(c.id)] }));
}

export interface UnresolvedColumn {
  header: string;
  /** Hours under this header across included, non-removed rows. */
  hours: number;
  rows: number;
}

/** Unrecognized columns that hold hours and have no decision yet. Each one blocks the import. */
export function unresolvedColumns(columns: ExcelImportColumn[], rows: ImportRowDraft[], columnMapping: ColumnMapping): UnresolvedColumn[] {
  const out: UnresolvedColumn[] = [];
  for (const column of columns) {
    if (column.kind !== 'unrecognized' || !column.hasNumbers) continue;
    if (columnMapping[column.header] !== undefined) continue;
    let hours = 0;
    let count = 0;
    for (const row of rows) {
      if (row.removed || !row.include) continue;
      const h = row.unmappedHours[column.header];
      if (h && h > 0) {
        hours += h;
        count++;
      }
    }
    out.push({ header: column.header, hours: roundTenths(hours), rows: count });
  }
  return out;
}

/** Hours under an unrecognized header across included, non-removed rows (for the strip's Ignore label). */
export function columnHours(header: string, rows: ImportRowDraft[]): { hours: number; rows: number } {
  let hours = 0;
  let count = 0;
  for (const row of rows) {
    if (row.removed || !row.include) continue;
    const h = row.unmappedHours[header];
    if (h && h > 0) {
      hours += h;
      count++;
    }
  }
  return { hours: roundTenths(hours), rows: count };
}

/** Review order: rows needing attention, then skipped duplicates, then everything else in sheet order. */
export function sortForReview(rows: ImportRowDraft[], issuesByRow: Record<number, ExcelImportIssue[]>): ImportRowDraft[] {
  const rank = (row: ImportRowDraft): number => {
    const status = rowStatus(issuesByRow[row.rowNumber] ?? [], row.include, row.removed);
    if (status === 'error') return 0;
    if (status === 'skipped' && row.isDuplicate) return 1;
    return 2;
  };
  return [...rows].sort((a, b) => rank(a) - rank(b) || a.rowNumber - b.rowNumber);
}

/** Plain list up to this many rows; beyond it, pages of PAGE_SIZE with exceptions always first. */
export const PAGINATE_ABOVE = 500;
export const PAGE_SIZE = 100;

/** DOM id of a review row, for scroll-to from the summary links and server refusals. */
export function rowDomId(rowNumber: number): string {
  return `credit-import-row-${rowNumber}`;
}

export function formatHours(h: number): string {
  return h.toFixed(1);
}

export function pluralActivities(n: number): string {
  return `${n} ${n === 1 ? 'activity' : 'activities'}`;
}
